import React, { useCallback, useMemo, useState } from "react"
import { MoveDownRight, MoveUpRight } from "lucide-react"

import { DataTable } from "components/data-table/data-table"
import { DataTableToolbar } from "components/data-table/data-table-toolbar"
import { DataTableColumnHeader } from "components/data-table/data-table-column-header"

import { useDataTable } from "@/hooks/shadcn/useDataTable"
import { cn } from "@/utils/shadcn-utils"
import { getLocalStringDateTime } from "@/utils/date"
import { formatMoney } from "@/utils/utils"
import { green, red } from "@/themes/colors"

import type { WalletTransaction } from "@/types/TransactionsType"
import {
  DetailedWalletTransaction,
  WALLET_TRANSACTION_DIRECTION_OPTIONS,
  WALLET_TRANSACTION_STATUS_OPTIONS,
} from "@/types/TransactionsType"
import { useAppDispatch, useAppSelector } from "@/hooks/reduxToolkitHook"
import { Column } from "@tanstack/react-table"
import { getDirectionLabel, getStatusConfig } from "@/utils/transactions/utils"
import Tag from "components/ui/tag"
import TransactionDetailSheet from "components/transactions/TransactionDetailSheet"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown-menu"
import { Button } from "components/ui/button"
import { MoreHorizOutlined } from "@mui/icons-material"
import CreditDemandValidationModal from "components/transactions/credit-demands/CreditDemandValidationModal"
import CreditDemandRejectionModal from "components/transactions/credit-demands/CreditDemandRejectionModal"
import { rejectTransaction, validateTransaction } from "@/redux/features/transactionsSlice"

function TransactionDataTable() {
  const dispatch = useAppDispatch()
  const { transactions } = useAppSelector(state => state.transactions)
  const [openPreview, setOpenPreview] = useState(false)
  const [openValidateModal, setOpenValidateModal] = useState(false)
  const [openRejectModal, setOpenRejectModal] = useState(false)


  const [selectedTransaction, setSelectedTransaction] =
    useState<DetailedWalletTransaction | null>(null)

  const handleValidate = useCallback(async (id: string, action: string) => {
    try {
      await dispatch(validateTransaction({ id, action })).unwrap()
    } catch (error) {
      console.error("Erreur validation", error)
    } finally {
    }
  }, [])

  const handleReject = useCallback(async (id: string, action: string, reason: string) => {
    try {
      await dispatch(rejectTransaction({ id, action, reason })).unwrap()
    } catch (error) {
      console.error("Erreur rejet", error)
    } finally {
      setSelectedTransaction(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }
  }, [])

  const handleViewModalOpen = (transaction: DetailedWalletTransaction) => {
    setSelectedTransaction(transaction)
    setOpenPreview(true)
  }
  const handleRejectionModalOpen = (transaction: DetailedWalletTransaction) => {
    setSelectedTransaction(transaction)
    setOpenRejectModal(true)
  }
  const handleValidationModalOpen = (transaction: DetailedWalletTransaction) => {
    setSelectedTransaction(transaction)
    setOpenValidateModal(true)
  }

  const columns = useMemo(
    () => [
      {
        id: "id",
        accessorKey: "reference",  // needed for search to work
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }: any) => {
          const isDebit = row.original.direction === "DEBIT"
          return (
            <div className="flex items-center gap-2">
              {isDebit ? (
                <MoveUpRight
                  color={red["500"]}
                  className="h-5 w-5 rounded-full bg-red-100 p-1"
                />
              ) : (
                <MoveDownRight
                  color={green["500"]}
                  className="h-5 w-5 rounded-full bg-green-100 p-1"
                />
              )}
              <span>{row.original.reference}</span>
            </div>
          )
        },
        size: 200,
        enableColumnFilter: true,
        meta: {
          label: "Référence",
          variant: "text",
          placeholder: "Rechercher par référence...",
        },
      },
      {
        accessorKey: "date",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Date" />
        ),
        cell: ({ row }: any) =>
          getLocalStringDateTime(row.original.created_at),
        size: 150,

      },
      {
        accessorKey: "company_name",  // ← match the actual field
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Client" />
        ),
        size: 130,
        cell: ({ row }: any) => (
          <span className="px-3 py-1 rounded-lg text-xs font-medium">
            {row.original.company_name}
          </span>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Client",
          variant: "multiSelect",
          options: Array.from(
            new Map(
              transactions.map((transaction: WalletTransaction) => [
                transaction.company_name,
                { value: transaction.company_name, label: transaction.company_name },
              ]),
            ).values(),
          ),
        },
        filterFn: (row: any, columnId: string, filterValue: string[]) => {
          return filterValue.includes(row.original.company_name)
        },
      },
      {
        id: "direction",
        accessorKey: "direction",
        header: ({ column }: { column: Column<WalletTransaction, unknown> }) => (
          <DataTableColumnHeader column={column} title="Type" />
        ),
        cell: ({ row }) => {
          const direction = row.original.direction
          const transactionType = row.original.transaction_type
          const label = getDirectionLabel(direction, transactionType)

          return (
            <div className="flex items-center gap-2">
              <span>{label}</span>
            </div>
          )
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Type",
          variant: "multiSelect",
          options: WALLET_TRANSACTION_DIRECTION_OPTIONS,
        },
        //@ts-ignore
        filterFn: (row: any, columnId: string, filterValue: string[]) => {
          return filterValue.includes(row.original.direction)
        },
        size: 250,
      },
      {
        accessorKey: "amount",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Montant" />
        ),
        cell: ({ row }: any) => {
          const isDebit = row.original.direction === "DEBIT"
          return (
            <span
              className={cn(
                "font-medium",
                isDebit &&
                "text-danger",
              )}
            >
            {isDebit ? "- " : "+ "}
              {formatMoney(Math.abs(row.original.amount))} F CFA
          </span>

          )
        },
        size: 180,
      },

      {
        id: "status",
        accessorKey: "status",
        header: ({ column }: { column: Column<WalletTransaction, unknown> }) => (
          <DataTableColumnHeader column={column} title="Statut" />
        ),
        cell: ({ row }) => {
          const status = row.original.status
          const config = getStatusConfig(status)
          return <Tag className="py-1.5 px-2"
                      type={config.color}>{config.label} {["PENDING", "VALIDATED"].includes(row.original.status) ? " (Demandé) " : ""} </Tag>
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Statut",
          variant: "multiSelect",
          options: WALLET_TRANSACTION_STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
        },
        //@ts-ignore
        filterFn: (row: any, columnId: string, filterValue: string[]) => {
          return filterValue.includes(row.original.status)
        },
        size: 190,
      },

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: any) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizOutlined sx={{ width: 20, height: 20 }} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {
                row.original.needs_validation && <>
                  <DropdownMenuItem
                    onClick={() => handleValidationModalOpen(row.original)}
                    className="cursor-pointer">
                    Valider
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleRejectionModalOpen(row.original)}
                    className="cursor-pointer text-red-600 focus:text-red-600">
                    Refuser
                  </DropdownMenuItem>
                </>
              }
              <DropdownMenuItem
                onClick={() => handleViewModalOpen(row.original)}
                className="cursor-pointer">
                Voir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        size: 80,
      },
    ],
    [transactions],
  )

  const table = useDataTable({
    data: transactions,
    columns,
    initialColumnPinning: { left: [""], right: ["actions"] },
  })


  return (
    <>
      <div className=" cards bg-white rounded-xl shadow-sm pt-8">
        <DataTable
          table={table}
          noDataMessage="Aucune transaction"
          tableLayout="fixed"
        >
          <DataTableToolbar table={table} />
        </DataTable>
      </div>
      {
        selectedTransaction && <>
          <TransactionDetailSheet
            transactionId={selectedTransaction.id}
            sheetOpen={openPreview}
            onSheetOpenChange={setOpenPreview}
            onReject={() => handleRejectionModalOpen(selectedTransaction!)}
            onValidate={() => handleValidationModalOpen(selectedTransaction!)}
          />
          <CreditDemandValidationModal
            open={openValidateModal}
            onClose={() => setOpenValidateModal(false)}
            onValidate={() => {
              handleValidate(selectedTransaction!.id, "approve").then(() => {
                setSelectedTransaction(null)
                setOpenValidateModal(false)
              })
            }}
            transaction={selectedTransaction}
          />
          <CreditDemandRejectionModal
            open={openRejectModal}
            onClose={() => setOpenRejectModal(false)}
            onReject={(rejectComment) => {
              handleReject(selectedTransaction!.id, "reject", rejectComment).then(() => {
                setSelectedTransaction(null)
                setOpenRejectModal(false)
              })
            }}
            transaction={selectedTransaction}
          />
        </>
      }
    </>
  )
}

export default TransactionDataTable